import type { TFunction } from 'i18next';
import { getReconFlagStateLabel } from '../utils/reconFlags.config';
import type { ReconFlagRow } from '../utils/reconFlags.types';
import {
  ACCOUNT_ORDER,
  ACTIVITY_ORDER,
  SYSTEM_ID_ORDER,
} from './reconFlagDrawerOverviewConstants';
import {
  activityKeysPresent,
  buildAdditionalSections,
  collectEntriesInOrder,
  issueSummaryText,
  mergeAccountShape,
  resolveActivitySource,
  resolveSystemSource,
  rowsFromOrderedObject,
  shouldShowAccountSection,
  systemKeysPresent,
} from './reconFlagDrawerOverviewDetails';
import {
  formatDateTimeValue,
  readString,
} from './reconFlagDrawerOverviewFormatting';
import type { ReconFlagOverviewSection } from './reconFlagDrawerOverviewTypes';

export const buildReconFlagOverviewSections = (
  t: TFunction,
  flag: ReconFlagRow,
): ReconFlagOverviewSection[] => {
  const consumedRoot = new Set<string>([
    'account',
    'activity',
    'systemIdentifiers',
    'system_identifiers',
  ]);

  const sections: ReconFlagOverviewSection[] = [];

  const issueText = issueSummaryText(flag);
  if (issueText) {
    sections.push({
      id: 'issue-summary',
      heading: t('reconFlags.drawer.sections.issueSummary'),
      rows: [],
      prose: issueText,
    });
  }

  const flagSummaryRows = [
    {
      label: t('reconFlags.drawer.fields.flagType'),
      value: readString(flag.type) ?? '—',
    },
    {
      label: t('reconFlags.drawer.fields.objectType'),
      value: readString(flag.objectType) ?? '—',
    },
    {
      label: t('reconFlags.drawer.fields.priority'),
      value: flag.priority
        ? t(`reconFlags.severityLabel.${flag.priority}`)
        : '—',
    },
    {
      label: t('reconFlags.drawer.fields.firstSeen'),
      value: formatDateTimeValue(flag.firstSeenAt),
    },
    {
      label: t('reconFlags.drawer.fields.state'),
      value: ((): string => {
        const s = readString(flag.state);
        if (!s) {
          return '—';
        }
        return getReconFlagStateLabel(t, s);
      })(),
      style: flag.state ? { fontWeight: 'bold' } : undefined,
    },
  ];
  sections.push({
    id: 'flag-summary',
    heading: t('reconFlags.drawer.sections.flagSummary'),
    rows: flagSummaryRows,
  });

  if (shouldShowAccountSection(flag)) {
    const merged = mergeAccountShape(flag) ?? {};
    const withoutUser = { ...merged };
    delete withoutUser.user;
    const rows = rowsFromOrderedObject(
      t,
      withoutUser,
      ACCOUNT_ORDER,
      true,
      true,
    );
    if (rows.length > 0) {
      sections.push({
        id: 'account',
        heading: t('reconFlags.drawer.sections.account'),
        rows,
      });
    }
  }

  const actInfo = resolveActivitySource(flag);
  if (actInfo && activityKeysPresent(actInfo.record)) {
    consumedRoot.add('activity');
    const rows = rowsFromOrderedObject(
      t,
      actInfo.record,
      ACTIVITY_ORDER,
      actInfo.appendRemainingScalars,
    );
    if (rows.length > 0) {
      for (const { key } of collectEntriesInOrder(
        actInfo.record,
        ACTIVITY_ORDER,
        actInfo.appendRemainingScalars,
      )) {
        consumedRoot.add(key);
      }
      sections.push({
        id: 'activity',
        heading: t('reconFlags.drawer.sections.activity'),
        rows,
      });
    }
  }

  const sysInfo = resolveSystemSource(flag);
  if (sysInfo && systemKeysPresent(sysInfo.record)) {
    const rows = rowsFromOrderedObject(
      t,
      sysInfo.record,
      SYSTEM_ID_ORDER,
      sysInfo.appendRemainingScalars,
    );
    if (rows.length > 0) {
      for (const { key } of collectEntriesInOrder(
        sysInfo.record,
        SYSTEM_ID_ORDER,
        sysInfo.appendRemainingScalars,
      )) {
        consumedRoot.add(key);
      }
      sections.push({
        id: 'system',
        heading: t('reconFlags.drawer.sections.systemIdentifiers'),
        rows,
      });
    }
  }

  for (const k of [
    'issueSummary',
    'issue_summary',
    'summary',
    'description',
    'issueTitle',
    'issue_title',
    'displayType',
    'display_type',
    'humanReadableType',
    'clientName',
    'client_name',
    'custodianName',
    'custodian',
    'custodianAccountNumber',
    'custodian_account_number',
  ]) {
    consumedRoot.add(k);
  }

  sections.push(...buildAdditionalSections(t, flag, consumedRoot));

  return sections;
};
